import { useEffect, useMemo, useState } from "react";

import Link from "next/link";
import { useRouter } from "next/router";

import { useMediaQuery, useToggle } from "@netfront/common-library";
import { useGoogleAnalytics, useToast } from "@netfront/ekardo-content-library";
import { Button } from "@netfront/ui-library";

import { Header, IconCaretDown, SideDrawer, UserMenu } from "@/components"
import { CONSTANTS } from "@/constants";
import { useAddCustomEvent, useGroups } from "@/hooks";
import { EAnalyticsEvent } from "@/interfaces/enums";
import { getValidClassNames } from "@/utils";
import { useDomain, useLogout } from "@netfront/gelada-identity-library";
import { IMenuItem } from "@/interfaces";

export const PrivateHeader = () => {
  const { asPath, push } = useRouter();
  const { hasHealthCoach, hasFeedback, userType } = useGroups();
  const { isToggled: isMobileMenuOpen, toggle: toggleMobileMenu } = useToggle();
  const { isToggled: isSubMenuToggled, toggle: toggleSubMenu } = useToggle();
  const { trackEvent } = useGoogleAnalytics();
  const { handleToastError } = useToast();
  const { getDomain } = useDomain();

  const [navItems, setNavItems] = useState<IMenuItem[]>([]);

  const isSmallViewport = useMediaQuery({
    size: "small",
  });

  const { handleLogout } = useLogout({
    onCompleted: () => {
      push('/').catch((error) =>
        handleToastError({
          error,
          shouldUseFriendlyErrorMessage: true,
        }),
      );
    },
  });

  
  const { handleAddCustomEvent } = useAddCustomEvent({
    onError: error => {
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true
      })
    }
  });

  const [activeMenuItemId, setActiveMenuItemId] = useState<string>();

  const onMouseEnter = (id: string) => {
    setActiveMenuItemId(id);
    toggleSubMenu();
  };

  const onMouseLeave = (event: React.MouseEvent) => {
    const menuLink = event.target as HTMLElement;

    const bounds = menuLink.getBoundingClientRect();
    const x = event.clientX - bounds.left;
    const y = event.clientY - bounds.top;
    const w = bounds.width;

    if (!isSubMenuToggled) {
      return;
    }

    if (x < 0) {
      toggleSubMenu();
    } else if (x > w) {
      toggleSubMenu();
    } else if (y < 0) {
      toggleSubMenu();
    }
  };

  const trackBookHeathCoachClick = () => {
    handleAddCustomEvent({ 
      projectId: String(process.env.REACT_APP_PROJECT_ID), 
      type: 'book-health-coach' 
    });
    trackEvent(EAnalyticsEvent.BookHealthCoachClick);
  };

  const STUDENT_ID = CONSTANTS.USER_TYPE.STUDENT.ID;
  const TEACHER_ID = CONSTANTS.USER_TYPE.TEACHER.ID;


  useEffect(() => {
    if(!userType) return;
    
    let menuItems: IMenuItem[] = CONSTANTS.MENU.PRIVATE;

    if(userType?.id === STUDENT_ID) {
      menuItems = [
        ...menuItems.filter(({ id }) => id !== 'goals' && id !== 'feedback' && id !== 'resources' && id !== 'about-us'),
        { id: 'feedback-overview', label: 'Personal Feedback', href: '/student/personal-feedback/overview' },
      ];
    }

    if(userType?.id === TEACHER_ID) {
      menuItems = [
        ...menuItems.filter(({ id }) => id !== 'goals' && id !== 'feedback' && id !== 'get-help-now' && id !== 'resources' && id !== 'about-us'),
        { id: 'resources', label: 'External Resources', href: '/resources' },
        { id: 'log-book', label: 'Logbook', href: '/teacher/logbook' },
        { id: 'evaluation', label: 'Evaluation', href: '/teacher/evaluation/introduction' },
      ];
    };

    if(!hasFeedback) {
      menuItems = menuItems.filter(({ id }) => id !== 'feedback') 
    }

    setNavItems(menuItems)
  }, [userType, hasFeedback])


  return(
    <Header logo={{ href: '/dashboard' }}>  
      <nav className="none xl:flex flex-1 justify-center">
        <ul className="flex list-style-none">
          {navItems.map(({ href, id, label, subItems }) => {
            return(
            <li 
              key={`public-header-${id}`} 
              className="m-0"
              {...(subItems && subItems.length && { onMouseEnter: () => onMouseEnter(id) })}
              {...(subItems && subItems.length && { onMouseLeave })}
            >
              <Link 
                className={getValidClassNames({
                  'flex items-center color-black weight-500 px-6 py-4 pointer text-decoration-none hover:color-primary': true,
                  'color-green-500': asPath.includes(String(href)),
                  'c-spotlight--feedback': id === 'feedback',
                  'c-spotlight--resources': id === 'resources'
                })} 
                href={String(href)}
              >
                {label}
                {Boolean(subItems?.length) && (
                <button
                  className={getValidClassNames({
                    'bg-transparent color-inherit border-none rounded bg-primary flex items-center justify-center p-0 transition-200 pointer':
                      true,
                    'rotate-180':  Boolean(activeMenuItemId === id && isSubMenuToggled && subItems?.length),
                  })}
                  onClick={toggleSubMenu}
                >
                  <span className="sr-only">Hover to toggle menu</span>
                  <IconCaretDown />
                </button>
              )}
              {activeMenuItemId === id && isSubMenuToggled && subItems?.length ? (
                  <div
                    key={`submenu-${id}`}
                    className="bg-green-100 py-6 absolute w-full left-0 top-full border-b-4 border-b-primary"
                    onMouseLeave={toggleSubMenu}
                  >
                    <div className="container container-xs animate__fadeIn">
                      <div className="flex items-start">
                        {subItems.map(({ className, href: subHref, label: subLabel }) => (
                          <Link
                            key={`submenu-item-${id}`}
                            className={getValidClassNames({
                              'flex items-center color-black weight-600 px-6 py-4 pointer text-decoration-none hover:color-primary': true,
                              [String(className)]: Boolean(className),
                            })}
                            href={String(subHref)}
                          >
                            {subLabel}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null}
              </Link>
            </li>
          )})}
        </ul>
      </nav>

      <div className="ml-auto flex items-center">
        {userType?.id === CONSTANTS.USER_TYPE.PARENT.ID && hasHealthCoach && (
          <Button
            additionalClassNames="c-spotlight--health-coach mr-4 md:mr-8"
            text="Book a health coach"
            linkButton={{ 
              id: 'coach', 
              url: 'https://outlook.office365.com/owa/calendar/Health4LifeParentsandTeensHealthCoachBookings@unisyd.onmicrosoft.com/bookings/',  
              target: '_blank' 
            }}
            variant="cta--tertiary"
            onClick={() => {
              trackBookHeathCoachClick();
            }}
            {...isSmallViewport && {
              size: 'small'
            }}
          />
        )}
        <UserMenu />
      </div>

      <div className="flex xl:none c-mobile-menu-button">
        <button 
          className="c-header-mobile-trigger color-black flex md:hidden items-center justify-center ml-auto bg-transparent border-none ml-2 pointer" 
          onClick={toggleMobileMenu}
        >
          <span className="sr-only">Open menu</span>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
        </button>

        {isMobileMenuOpen && (
          <SideDrawer 
            logo={{
              href: '/dashboard'
            }}
            onClose={toggleMobileMenu}
          >
            <nav aria-label="Mobile navigation" className="m-auto">
              <ul className="flex flex-col items-center list-style-none">
                {navItems.map(({ href, id, label }) => (
                  <li key={`mobile-menu-item-${id}`} className="ml-0">
                    <Link 
                      className="flex items-center color-black weight-600 px-6 py-4 text-decoration-none hover:color-primary" 
                      href={String(href)}
                      onClick={() => toggleMobileMenu()}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="mt-auto flex flex-col items-center justify-center py-4">
              {hasHealthCoach && (
                <Button
                  additionalClassNames="mb-8"
                  text="Book a health coach"
                  linkButton={{ 
                    id: 'coach', 
                    url: 'https://outlook.office365.com/owa/calendar/Health4LifeParentsandTeensHealthCoachBookings@unisyd.onmicrosoft.com/bookings/',  
                    target: '_blank' 
                  }}
                  variant="cta--tertiary"
                  onClick={() => {
                    trackBookHeathCoachClick();
                  }}
                />
              )}

              <Button
                text="Logout"
                onClick={() => {
                  handleLogout({ 
                    domain: getDomain(), 
                    path: '/' 
                  })
                }}
              />
            </div>
          </SideDrawer>
        )}
      </div>
    </Header>
  )
};